import { useEffect, useState } from 'react';
import type { TelegramApi } from '../../telegram/webapp';
import s from './onboarding.module.css';

export const ONBOARDING_KEY = 'onboarding';

const SLIDES = [
  {
    title: 'Пропуск за десять секунд',
    text: 'Нажмите «Заказать пропуск» и впишите номер машины или имя гостя. Охрана увидит пропуск сразу.',
  },
  {
    title: 'Не знаете номер — отправьте ссылку',
    text: 'Кнопка «Отправить ссылку гостю»: гость сам впишет номер машины, а вы увидите, когда он приедет.',
  },
  {
    title: 'Пропуска и связь с УК',
    text: 'Во вкладке «Пропуска» — кто ожидается и кто уже на территории. В «Обращениях» — переписка с управляющей компанией.',
  },
];

/** Set once the intro is closed in this launch: the home screen remounts, CloudStorage may lag behind. */
let seenThisLaunch = false;

/** Tests only: forget the marks between cases. */
export function resetOnboardingMarks() {
  seenThisLaunch = false;
  try {
    window.localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    // ignore
  }
}

function readLocalMark(): boolean {
  try {
    return window.localStorage.getItem(ONBOARDING_KEY) !== null;
  } catch {
    return false;
  }
}

function writeLocalMark() {
  try {
    window.localStorage.setItem(ONBOARDING_KEY, 'done');
  } catch {
    // Storage blocked: the launch flag and CloudStorage still hold the mark.
  }
}

/**
 * A short skippable introduction shown once: the mini app looks unlike the PASS24 mobile app.
 * The "seen" mark lives in Telegram CloudStorage (survives reinstall and device change) and is mirrored
 * on the device and for the launch: CloudStorage reads null on timeout, error and old clients, and such
 * a read alone must not bring the intro back. `replay` opens it on request («Ещё» → «Как пользоваться»).
 */
export function Onboarding({ telegram, replay = false, onClose }: { telegram: TelegramApi; replay?: boolean; onClose?: () => void }) {
  const [open, setOpen] = useState(replay);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (replay) {
      setIndex(0);
      setOpen(true);
      return;
    }
    if (seenThisLaunch || readLocalMark()) return;
    let alive = true;
    void telegram.storage.get(ONBOARDING_KEY).then((seen) => {
      if (seen !== null) writeLocalMark();
      else if (alive && !seenThisLaunch) setOpen(true);
    });
    return () => {
      alive = false;
    };
  }, [telegram, replay]);

  if (!open) return null;
  const finish = () => {
    setOpen(false);
    seenThisLaunch = true;
    writeLocalMark();
    void telegram.storage.set(ONBOARDING_KEY, 'done');
    onClose?.();
  };
  const last = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  return (
    <div className={s.backdrop}>
      <section role="dialog" aria-modal="true" aria-labelledby="onboarding-title" className={s.sheet}>
        <div className={s.dots} aria-hidden="true">
          {SLIDES.map((_, i) => (
            <span key={i} className={i === index ? `${s.dot} ${s.dotOn}` : s.dot} />
          ))}
        </div>
        <h2 id="onboarding-title" className={s.title}>
          {slide.title}
        </h2>
        <p className={s.text}>{slide.text}</p>
        <button type="button" className={s.next} onClick={() => (last ? finish() : setIndex(index + 1))}>
          {last ? 'Начать' : 'Далее'}
        </button>
        {!last && (
          <button type="button" className={s.skip} onClick={finish}>
            Пропустить
          </button>
        )}
      </section>
    </div>
  );
}
